import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Box, SideDrawer, withStyles } from 'vopay';
import { useBlockingLoader } from '#shared/providers/Loader';
import { Permissions, PermissionValues } from '#shared/components/Permissions';
import { Skeleton } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useToast } from '#shared/providers/Toast';
import useSubAccountList from '../useSubAccountList';
import styles from './AccountPermissionsDrawer.module.scss';

// Ported from client-dashboard's SubAccountsList AccountPermissionsDrawer (ID-13193). Shared's
// Skeleton/Toast/AlertDialog/Loader replace the host's, the logged-in client's Country comes from the
// feature's `account.country` instead of useClientData(), and the fetch / adapt / label-bake moved behind the
// host's `permissionsEditor` prop — the raw permissions shape and its adapter are the host's domain.

// Lays the shared permissions editor out in the drawer over the host's adapted sections, and saves.

const cn = withStyles(styles);

const SKELETON_ROW_COUNT = 6;

type AccountPermissionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  accountID?: string;
  accountName?: string;
  currency?: string;
};

export default function AccountPermissionsDrawer({
  isOpen,
  onClose,
  accountID,
  accountName,
  currency,
}: AccountPermissionsDrawerProps) {
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();

  const {
    account,
    permissionsEditor: { sections, fetchingPermissions, fetchPermissions, savePermissions },
  } = useSubAccountList();

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList.Permissions' });

  useEffect(() => {
    if (!accountID || !isOpen) return;

    const loadPermissions = async () => {
      try {
        await fetchPermissions(accountID);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('Errors.fetchPermissions', { defaultValue: 'Failed to load permissions. Please try again.' });

        alertDialog.error(t('Errors.title', { defaultValue: 'Error' }), errorMessage);
      }
    };

    loadPermissions();
  }, [accountID, isOpen]);

  const handleSave = async (values: PermissionValues) => {
    if (!accountID) return;

    blockingLoader.show();

    try {
      await savePermissions(accountID, values);
      onClose();
      toast.success(t('Success.permissionsUpdated', { defaultValue: 'Permissions updated successfully' }));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('Errors.savePermissions', { defaultValue: 'Failed to save permissions. Please try again.' });

      alertDialog.error(t('Errors.title', { defaultValue: 'Error' }), errorMessage);
    } finally {
      blockingLoader.hide();
    }
  };

  return (
    <Permissions
      sections={sections}
      currency={currency || (account.country === 'US' ? 'USD' : 'CAD')}
      formID="accountPermissionForm"
      onSave={handleSave}
    >
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        headerClassName={cn('whiteBackground')}
        footerClassName={cn('whiteBackground')}
        title={
          <div className={cn('header')}>
            <span>{t('Drawer.title', { defaultValue: 'Edit Permissions' })}</span>

            {accountName && (
              <BodyText weight="md" className={cn('accountName')}>
                {accountName}
              </BodyText>
            )}
          </div>
        }
        headerFullWidthContent={
          <div className={cn('searchWrapper')}>
            <Permissions.Search />
          </div>
        }
        footer={
          <div className={cn('footer')}>
            <Permissions.EnableAllButton disabled={fetchingPermissions} />
            <Permissions.SaveButton disabled={fetchingPermissions} />
          </div>
        }
      >
        {fetchingPermissions ? (
          <Box flx="col" gap="sm">
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <Skeleton key={index} height="56px" />
            ))}
          </Box>
        ) : (
          <Permissions.Sections />
        )}
      </SideDrawer>
    </Permissions>
  );
}
